import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IncidentDetailsPageStore } from '../incident-details-page.store';
import { MatIconModule } from '@angular/material/icon';
import { MediaReferenceMetadataModel } from '@app/models/media-reference-metadata.model';
import { MediaReferenceHelperService } from '../references/media-references/media-reference-helper.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { CountryDescriptor, CountryFlags } from '@app/enums/country';
import {
  FIELD_REFERENCE_NAMES_BY_ROW_FIELD,
  MergeRow,
  MergeRowBase,
  ReferenceRow,
  RimsPhaseRow,
  RimsStepExpandableRow,
  RimsStepRow,
  SingleColumnMergeRow,
} from './models/merge-row.model';
import { TopHeaderColumn } from './models/merge-header-column.model';
import { IncidentModel } from '@app/models/incident.model';
import { IncidentsService } from '@app/services/incidents/incidents.service';
import { catchError, firstValueFrom, of } from 'rxjs';
import { buildMergeViewRows, buildRimsStepRows } from './merge-view-rows';
import { entityRowDefinitions, RowDefinition, threatActorRowDefinitions } from './merge-view-row-definitions';
import { RimsService } from '@app/services/rims/rims.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '@app/components/confirmation-modal/confirmation-modal.component';
import { ToastrService } from 'ngx-toastr';
import { RimsPhaseInfo, RimsStepSchema } from '@app/models/rims.model';
import { setCheckedFieldToMergedIncident, setCheckedRimsSetItemToMergedIncident } from './merge-view-merging';
import { ArticleReferenceModel } from '@app/models/article-reference.model';
import { MediaReferenceMetadataCreateModel } from '../models/media-reference-metadata-create.model';
import { FieldReferenceService } from '../field-references.service';
import { MenuItemsCollection, SectionMenuComponent } from '@app/components/section-menu/section-menu.component';
import { capitalizeRimsPath } from '@app/utils/rims/rims.utils';

// Label used in the row headers to indicate that no merging value was selected for that row (it's the manual value).
const MANUALLY_CHANGED_DESCRIPTION = 'Manually changed';

@Component({
  standalone: true,
  selector: 'app-merge-view',
  imports: [CommonModule, MatIconModule, MatMenuModule, MatSlideToggleModule, FormsModule, SectionMenuComponent],
  providers: [MediaReferenceHelperService],
  templateUrl: './merge-view.component.html',
  styleUrl: './merge-view.component.scss',
})
export class MergeViewComponent implements OnInit, AfterViewInit {
  @ViewChildren('textExpandableContent') private textExpandableContents!: QueryList<ElementRef>;
  @Output() formFieldMerged = new EventEmitter<void>();

  protected readonly sectionMenuItems: MenuItemsCollection = {
    Details: 'details',
    'Affected Entity': 'affected-entity',
    Rims: 'rims',
    'Threat Actor': 'threat-actor',
    References: 'references',
  };

  // We only keep one RIMS section expanded at a time.
  protected expandedRimsStepId: string | null = null;

  protected CountryFlags: Record<string, string> = CountryFlags;
  protected CountryDescriptor: Record<string, string> = CountryDescriptor;

  protected topHeaderColumns: TopHeaderColumn[] = [];
  protected detailsRows: MergeRow[] = [];
  protected entityRows: MergeRow[] = [];
  protected threatActorRows: MergeRow[] = [];
  protected articleReferenceRows: ReferenceRow[] = [];
  protected mediaReferenceRows: ReferenceRow[] = [];
  protected rimsPhaseSections: RimsPhaseRow[] = [];
  protected rimsStepRows: Partial<Record<string, RimsStepRow[]>> = {};
  protected rowSelectionCheckboxVisibility = new WeakMap<MergeRowBase, boolean>();

  private rimsPhaseInfos: RimsPhaseInfo[] = [];
  private loadingRimsStepIds = new Set<string>();
  private expandedRimsStepNestedFields = new Set<string>();
  private originalMergeRowDescriptions = new WeakMap<MergeRow, string | undefined>();
  // Cached per stepId so RIMS field values checked/reset in merge-view-merging.ts can be display-formatted (e.g. dates) the same way rows are built.
  private rimsStepSchemas: Record<string, RimsStepSchema> = {};

  constructor(
    protected readonly mediaReferenceHelper: MediaReferenceHelperService,
    private readonly incidentDetailsPageStore: IncidentDetailsPageStore,
    private readonly fieldRefService: FieldReferenceService,
    private readonly incidentsService: IncidentsService,
    private readonly rimsService: RimsService,
    private readonly dialog: MatDialog,
    private readonly toastr: ToastrService,
  ) {
    effect(() => {
      this.rebuildRowsFromState();
    });
  }

  public async ngOnInit(): Promise<void> {
    await this.loadRimsPhaseInfosAsync();
  }

  public ngAfterViewInit(): void {
    this.scheduleOverflowStateUpdate();
    this.textExpandableContents.changes.subscribe(() => this.scheduleOverflowStateUpdate());
  }

  private scheduleOverflowStateUpdate(): void {
    setTimeout(() => this.updateOverflowStates());
  }

  private updateOverflowStates(): void {
    if (!this.textExpandableContents) {
      return;
    }

    const elements = this.textExpandableContents.toArray();
    let idx = 0;

    for (const rows of [this.detailsRows, this.entityRows]) {
      for (const row of rows) {
        if (row.type === 'text-expandable') {
          for (const col of row.columns) {
            if (idx < elements.length) {
              col.isOverflowing = elements[idx].nativeElement.scrollHeight > 100;
              idx++;
            }
          }
        }
      }
    }
  }

  public rebuildRowsFromState(): void {
    const mergedIncident = this.incidentDetailsPageStore.incident();
    const originalIncident = this.incidentDetailsPageStore.mergeViewOriginal();
    const duplicateIncidents = this.incidentDetailsPageStore.mergeViewDuplicates();

    if (!mergedIncident || !originalIncident || duplicateIncidents == null) {
      this.clearRows();
      return;
    }

    mergedIncident.articleReferences = this.incidentDetailsPageStore.allArticleReferences();
    mergedIncident.mediaReferences = this.incidentDetailsPageStore.allMediaReferences();

    this.generateRowsFromIncidents(mergedIncident, originalIncident, duplicateIncidents);
    this.scheduleOverflowStateUpdate();
  }

  protected async detachIncidentAsync(incidentId: number): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: 'Detaching may change data',
        content: `Processing rules will be reapplied, and manual edits may be lost. Back up any manual edits before continuing.`,
        confirmButtonText: 'Detach incident',
      },
    });

    const dialogResult = await firstValueFrom(dialogRef.afterClosed());
    if (dialogResult == true) {
      const updatedMasterIncident = await firstValueFrom(
        this.incidentsService.detachIncident(incidentId).pipe(
          catchError(() => {
            this.toastr.error(
              `Failed to detach incident with id: ${incidentId}. Please try again.`,
              'Incident detach.',
              {
                positionClass: 'toast-bottom-right',
              },
            );

            return of(null);
          }),
        ),
      );

      if (updatedMasterIncident) {
        // update store
        this.incidentDetailsPageStore.setIncident(updatedMasterIncident);
        this.incidentDetailsPageStore.setMergeViewCacheData(
          this.incidentDetailsPageStore.mergeViewOriginal()!,
          this.incidentDetailsPageStore.mergeViewDuplicates()!.filter((incident) => incident.id !== incidentId),
        );

        // update the list of duplicate incident IDs to detach
        this.incidentDetailsPageStore.duplicateIncidentIdsToDetach.update((idsForDetachment) => [
          ...(idsForDetachment ?? []),
          incidentId,
        ]);

        // rebuild table rows
        this.rebuildRowsFromState();

        // important to mark as edited since the new detached state may contain different field references
        this.incidentDetailsPageStore.isFieldReferencesEdited = true;
      }
    }
  }

  protected toggleExpand(row: MergeRow): void {
    row.isExpanded = !row.isExpanded;
  }

  protected async toggleRimsStepExpand(stepId: string): Promise<void> {
    if (this.expandedRimsStepId === stepId) {
      this.expandedRimsStepId = null;
      return;
    }

    this.expandedRimsStepId = stepId;

    if (Object.hasOwn(this.rimsStepRows, stepId) || this.loadingRimsStepIds.has(stepId)) {
      return;
    }

    await this.loadRimsStepRowsAsync(stepId);
  }

  protected isRimsStepExpanded(stepId: string): boolean {
    return this.expandedRimsStepId === stepId;
  }

  protected toggleRimsStepNestedSectionExpand(field: string): void {
    if (this.expandedRimsStepNestedFields.has(field)) {
      this.expandedRimsStepNestedFields.delete(field);
    } else {
      this.expandedRimsStepNestedFields.add(field);
    }
  }

  protected isRimsStepNestedSectionExpanded(field: string): boolean {
    return this.expandedRimsStepNestedFields.has(field);
  }

  protected asArticleReferenceModel(value: unknown): ArticleReferenceModel | null {
    // We don't check for the id property because the article reference model can be a new model that doesn't have an id yet.
    if (typeof value === 'object' && value !== null && 'url' in value && 'title' in value) {
      return value as ArticleReferenceModel;
    }

    return null;
  }

  protected asMediaReferenceMetadataOrCreate(
    value: unknown,
  ): MediaReferenceMetadataModel | MediaReferenceMetadataCreateModel | null {
    if (typeof value === 'object' && value !== null && 'id' in value && 'contentType' in value) {
      return value as MediaReferenceMetadataModel;
    }

    if (typeof value === 'object' && value !== null && 'createId' in value && 'file' in value) {
      return value as MediaReferenceMetadataCreateModel;
    }

    return null;
  }

  protected asSlideToggleValue(value: unknown): boolean | null {
    if (typeof value === 'boolean' && (value === false || value === true)) {
      return value;
    }

    return null;
  }

  protected asMergeRows(value: unknown): MergeRow[] {
    return value as MergeRow[];
  }

  protected asStaircaseRows(value: unknown): (MergeRow | SingleColumnMergeRow)[] {
    return value as (MergeRow | SingleColumnMergeRow)[];
  }

  protected isSingleColumnMergeRow(row: MergeRow | SingleColumnMergeRow): row is SingleColumnMergeRow {
    return 'column' in row;
  }

  protected asCountryValue(value: unknown): { countryCode: string; countryName: string } | null {
    if (typeof value === 'object' && value !== null && 'countryCode' in value && 'countryName' in value) {
      return value as { countryCode: string; countryName: string };
    }

    return null;
  }

  protected calculateHeaderDepth(initialPaddingValuePx: number, depth: number | undefined): number {
    return initialPaddingValuePx + (depth ?? 0) * 20;
  }

  private computeRowSelectionCheckboxVisibility(row: MergeRowBase): boolean {
    // For now, the only template that does not support row selection checkboxes is the slide-toggle template.
    // The country code column should not have checkboxes due to the fact that it comes from the entity. Entity should be selectable.
    // If the row doesn't have an assigned field path, then it's a field that is not mergable.
    if (row.type === 'slide-toggle' || !row.field || row.field === 'entity.countryCode') {
      return false;
    }

    if (this.isMergeRow(row)) {
      // If merged column does not include the selected RIMS step, this row cannot be merged into merged incident.
      if (row.columns[0]?.isStepNotIncluded) {
        return false;
      }

      // One of the checkboxes is ticked. That means that we should show the checkboxes.
      if (row.columns.some((column) => column.isChecked)) {
        return true;
      }

      // Actors across the incidentsare different. Show the checkboxes.
      if (row.field === 'custom.actor') {
        const actorMerged = this.incidentDetailsPageStore.incident()?.actorId;
        const actorOriginal = this.incidentDetailsPageStore.mergeViewOriginal()?.actorId;
        const actorsDuplicates = this.incidentDetailsPageStore
          .mergeViewDuplicates()
          ?.map((incident) => incident.actorId);

        const uniqueActorIds = new Set([actorMerged, actorOriginal, ...(actorsDuplicates ?? [null])]);
        return uniqueActorIds.size > 1;
      }

      if (row.field === 'custom.entity') {
        const entityMerged = this.incidentDetailsPageStore.incident()?.entityId;
        const entityOriginal = this.incidentDetailsPageStore.mergeViewOriginal()?.entityId;
        const entitiesDuplicates = this.incidentDetailsPageStore
          .mergeViewDuplicates()
          ?.map((incident) => incident.entityId);

        const uniqueEntityIds = new Set([entityMerged, entityOriginal, ...(entitiesDuplicates ?? [null])]);
        return uniqueEntityIds.size > 1;
      }

      // All columns have the same value. No need to show merging
      if (this.areAllColumnValuesEquivalent(row)) {
        return false;
      }
    }

    // The rest of the cases like the references always show checkboxes
    return true;
  }

  private refreshAllRowSelectionCheckboxVisibility(): void {
    this.rowSelectionCheckboxVisibility = new WeakMap<MergeRowBase, boolean>();

    for (const row of this.detailsRows) {
      this.refreshRowSelectionCheckboxVisibilityForRow(row);
    }

    for (const row of this.entityRows) {
      this.refreshRowSelectionCheckboxVisibilityForRow(row);
    }

    for (const row of this.threatActorRows) {
      this.refreshRowSelectionCheckboxVisibilityForRow(row);
    }

    for (const stepRows of Object.values(this.rimsStepRows)) {
      this.refreshRimsStepRowCheckboxVisibility(stepRows ?? []);
    }
  }

  private refreshRimsStepRowCheckboxVisibility(stepRows: RimsStepRow[]): void {
    for (const stepRow of stepRows) {
      if (stepRow.kind === 'field') {
        // Fields nested in a set item never get their own checkbox; selection there is handled at the set-item level.
        if (!stepRow.isSetItemField) {
          this.refreshRowSelectionCheckboxVisibilityForRow(stepRow.row);
        }
      } else {
        this.refreshRimsStepRowCheckboxVisibility(stepRow.children);
      }
    }
  }

  private refreshRowSelectionCheckboxVisibilityForRow(row: MergeRow): void {
    if (!this.originalMergeRowDescriptions.has(row)) {
      this.originalMergeRowDescriptions.set(row, row.description);
    }

    this.rowSelectionCheckboxVisibility.set(row, this.computeRowSelectionCheckboxVisibility(row));
    this.refreshMergeRowDescription(row);
  }

  private refreshMergeRowDescription(row: MergeRow): void {
    // RIMS rows with missing merged step keep an empty description.
    if (row.field?.startsWith('rims.') && (row.columns[0]?.isStepNotIncluded ?? false)) {
      row.description = undefined;
      return;
    }

    const originalDescription = this.originalMergeRowDescriptions.get(row);
    const fieldReferenceNames = this.getFieldReferenceNames(row);
    const hasManualFieldReference = this.incidentDetailsPageStore
      .incident()
      ?.fieldReferences?.some(
        (fieldReference) => fieldReferenceNames.includes(fieldReference.name) && fieldReference.state === 'Manual',
      );

    if (!hasManualFieldReference) {
      row.description = originalDescription;
      return;
    }

    const areAllColumnsUnchecked = row.columns.every((column) => column.isChecked !== true);
    row.description = areAllColumnsUnchecked ? MANUALLY_CHANGED_DESCRIPTION : originalDescription;
  }

  private getFieldReferenceNames(row: MergeRow): string[] {
    if (row.field?.startsWith('rims.')) {
      return [capitalizeRimsPath(row.field.slice('rims.'.length))];
    }

    return row.field ? (FIELD_REFERENCE_NAMES_BY_ROW_FIELD[row.field] ?? []) : [];
  }

  private isMergeRow(row: MergeRowBase): row is MergeRow {
    return 'columns' in row;
  }

  private areAllColumnValuesEquivalent(row: MergeRow): boolean {
    if (row.columns.length === 0) {
      return false;
    }

    const values = row.columns.map((column) => column.value);

    // Do not render checkbox if every value is null.
    if (values.every((value) => !value)) {
      return true;
    }

    const firstValue = values[0];

    if (typeof firstValue === 'string') {
      return values.every((value) => value === firstValue);
    }

    if (typeof firstValue === 'boolean') {
      return values.every((value) => value === firstValue);
    }

    if (typeof firstValue === 'object' && firstValue !== null && 'countryCode' in firstValue) {
      return values.every(
        (value) =>
          typeof value === 'object' &&
          value !== null &&
          'countryCode' in value &&
          value.countryCode === firstValue.countryCode,
      );
    }

    return false;
  }

  protected onRowSelectionCheckboxChange(row: MergeRow, columnIndex: number, event: Event): void {
    // If we select a checkbox in a row, we want to uncheck all other checkboxes in that row.
    if (columnIndex <= 0 || row.type === 'slide-toggle') {
      return;
    }

    const inputElement = event.target as HTMLInputElement | null;
    const isChecked = inputElement?.checked ?? false;

    for (const column of row.columns) {
      column.isChecked = false;
    }

    if (isChecked) {
      row.columns[columnIndex].isChecked = true;
    }

    // After that we want to transfer the checked value to the merged incident.
    const isMergeApplied = setCheckedFieldToMergedIncident(
      row,
      this.incidentDetailsPageStore,
      this.fieldRefService,
      () => this.formFieldMerged.emit(),
      this.rimsStepSchemas,
    );
    if (isMergeApplied) {
      if (row.field === 'custom.entity') {
        // If we changed the entity, we also want to update the country code since it's part of the entity.
        const countryCodeRow = this.detailsRows.find((r) => r.field === 'entity.countryCode');
        if (countryCodeRow) {
          this.onRowSelectionCheckboxChange(countryCodeRow, columnIndex, event);
        }

        // Changing the entity in the UI is the name of the entity only. But we need to update all entity columns to be selected.
        const entityRows = this.entityRows.filter((r) => r.field !== 'custom.entity');
        for (const entityRow of entityRows) {
          this.onRowSelectionCheckboxChange(entityRow, columnIndex, event);
        }

        this.refreshMergedSectionRows(this.entityRows, entityRowDefinitions);
        this.syncSectionRowDescriptions(this.entityRows, row);
      } else if (row.field === 'custom.actor') {
        // Changing the actor in the UI is the name of the actor only. But we need to update all actor columns to be selected.
        const actorRows = this.threatActorRows.filter((r) => r.field !== 'custom.actor');
        for (const actorRow of actorRows) {
          this.onRowSelectionCheckboxChange(actorRow, columnIndex, event);
        }
        this.refreshMergedSectionRows(this.threatActorRows, threatActorRowDefinitions);
        this.syncSectionRowDescriptions(this.threatActorRows, row);
      } else {
        // Keep the merge grid stable: update only the merged cell in this row.
        row.hasConflict = !this.areAllColumnValuesEquivalent(row);
        this.refreshRowSelectionCheckboxVisibilityForRow(row);
      }
    }
  }

  protected onReferenceSelectionCheckboxChange(row: SingleColumnMergeRow, event: Event): void {
    if (row.columnIndex <= 0) {
      return;
    }

    const inputElement = event.target as HTMLInputElement | null;
    row.column.isChecked = inputElement?.checked ?? false;

    // After that we want to transfer the checked value to the merged incident.
    setCheckedFieldToMergedIncident(
      row,
      this.incidentDetailsPageStore,
      this.fieldRefService,
      () => this.formFieldMerged.emit(),
      this.rimsStepSchemas,
    );
  }

  protected onRimsSetCheckboxSelected(itemRow: RimsStepExpandableRow, columnIndex: number, event: Event): void {
    // If we select a checkbox for this set item, we want to uncheck all other checkboxes for it.
    if (columnIndex <= 0) {
      return;
    }

    const inputElement = event.target as HTMLInputElement | null;
    const isChecked = inputElement?.checked ?? false;
    const columns = itemRow.columns ?? [];

    for (const column of columns) {
      column.isChecked = false;
    }

    if (isChecked) {
      columns[columnIndex].isChecked = true;
    }

    const isMergeApplied = setCheckedRimsSetItemToMergedIncident(
      itemRow,
      this.incidentDetailsPageStore,
      this.fieldRefService,
    );
    if (isMergeApplied && this.expandedRimsStepId) {
      // Unchecking reverts/removes data on the merged incident rather than copying a known column's values, so the
      // whole step is rebuilt from the (now updated) incident data to keep nested values and hasValue flags correct.
      this.rebuildExpandedRimsStepRows(this.expandedRimsStepId);
    }
  }

  protected isRimsSetItemMatched(itemRow: RimsStepExpandableRow): boolean {
    // Show per-column checkboxes whenever a non-merged incident has data for this set item: either it matches
    // the merged incident's item, or the merged incident is missing this item entirely.
    const columns = itemRow.columns ?? [];
    return columns.slice(1).some((column) => column.hasValue);
  }

  private rebuildExpandedRimsStepRows(stepId: string): void {
    const mergedIncident = this.incidentDetailsPageStore.incident();
    const originalIncident = this.incidentDetailsPageStore.mergeViewOriginal();
    const duplicateIncidents = this.incidentDetailsPageStore.mergeViewDuplicates();
    const stepSchema = this.rimsStepSchemas[stepId];

    if (!mergedIncident || !originalIncident || duplicateIncidents == null || !stepSchema) {
      return;
    }

    this.rimsStepRows[stepId] = buildRimsStepRows(
      stepId,
      stepSchema,
      mergedIncident,
      originalIncident,
      duplicateIncidents,
    );
    this.refreshRimsStepRowCheckboxVisibility(this.rimsStepRows[stepId] ?? []);
  }

  private refreshMergedSectionRows(rows: MergeRow[], definitions: RowDefinition[]): void {
    const mergedIncident = this.incidentDetailsPageStore.incident();
    if (!mergedIncident) {
      return;
    }

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      const rowDefinition = definitions[index];

      if (!row || !rowDefinition || row.columns.length === 0) {
        continue;
      }

      row.columns[0].value = rowDefinition.valueSelector(mergedIncident) ?? null;
      row.hasConflict = !this.areAllColumnValuesEquivalent(row);
      this.refreshRowSelectionCheckboxVisibilityForRow(row);
    }
  }

  private syncSectionRowDescriptions(rows: MergeRow[], selectedRow: MergeRow): void {
    for (const row of rows) {
      if (row !== selectedRow) {
        row.description = selectedRow.description;
      }
    }
  }

  private clearRows(): void {
    this.expandedRimsStepId = null;
    this.topHeaderColumns = [];
    this.detailsRows = [];
    this.entityRows = [];
    this.threatActorRows = [];
    this.articleReferenceRows = [];
    this.mediaReferenceRows = [];
    this.rimsPhaseSections = [];
    this.rimsStepRows = {};
    this.rimsStepSchemas = {};
    this.rowSelectionCheckboxVisibility = new WeakMap<MergeRowBase, boolean>();
    this.originalMergeRowDescriptions = new WeakMap<MergeRow, string | undefined>();
    this.loadingRimsStepIds.clear();
    this.expandedRimsStepNestedFields.clear();
  }

  private async loadRimsPhaseInfosAsync(): Promise<void> {
    try {
      this.rimsPhaseInfos = await firstValueFrom(this.rimsService.getRimsPhaseInfos());
    } catch {
      this.rimsPhaseInfos = [];
    }

    this.rebuildRowsFromState();
  }

  private async loadRimsStepRowsAsync(stepId: string): Promise<void> {
    const mergedIncident = this.incidentDetailsPageStore.incident();
    const originalIncident = this.incidentDetailsPageStore.mergeViewOriginal();
    const duplicateIncidents = this.incidentDetailsPageStore.mergeViewDuplicates();

    if (!mergedIncident || !originalIncident || duplicateIncidents == null) {
      return;
    }

    this.loadingRimsStepIds.add(stepId);

    try {
      const stepSchema = await firstValueFrom(this.rimsService.getRimsStepSchema(stepId, true));
      this.rimsStepSchemas[stepId] = stepSchema;
      this.rimsStepRows[stepId] = buildRimsStepRows(
        stepId,
        stepSchema,
        mergedIncident,
        originalIncident,
        duplicateIncidents,
      );
    } catch {
      this.rimsStepRows[stepId] = [];
    }

    this.refreshRimsStepRowCheckboxVisibility(this.rimsStepRows[stepId] ?? []);
  }

  private generateRowsFromIncidents(
    mergedIncident: IncidentModel,
    originalIncident: IncidentModel,
    duplicateIncidents: IncidentModel[],
  ): void {
    this.expandedRimsStepId = null;
    this.rimsStepRows = {};
    this.originalMergeRowDescriptions = new WeakMap<MergeRow, string | undefined>();
    this.loadingRimsStepIds.clear();
    this.expandedRimsStepNestedFields.clear();
    const rows = buildMergeViewRows(mergedIncident, originalIncident, duplicateIncidents, this.rimsPhaseInfos);

    this.topHeaderColumns = rows.topHeaderColumns;
    this.detailsRows = rows.detailsRows;
    this.entityRows = rows.entityRows;
    this.threatActorRows = rows.threatActorRows;
    this.articleReferenceRows = rows.articleReferenceRows;
    this.mediaReferenceRows = rows.mediaReferenceRows;
    this.rimsPhaseSections = rows.rimsPhaseRows;
    this.refreshAllRowSelectionCheckboxVisibility();
  }
}
